import { DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';

import { CommonStyles } from '~common/react/CommonStyles';
import { getHeightForRows, SortableDataGrid } from '~common/react/components/dataGrid/SortableDataGrid3';
import { PlainTextCell } from '~src/app/spaces/ListView/dataGridCells/PlainTextAndNumberCells';
import { PlainTextHeader } from '~src/app/spaces/ListView/dataGridCells/PlainTextHeader';
import { Messages } from '~src/codegen/Localize';
import { cssClass, flexColumnStyle } from '~src/style';
import { SliceableHelper } from '~src/ts/helpers/SliceableHelper';
import { ISliceable } from '~src/ts/models/ISliceable';
import { IOrderClause } from '~src/ts/ServerApi/types/IOrderClause';

import { PublishedConnectionTaskTableInfo } from '../../types/PublishedConnectionTaskTableInfo';

const cssPrefix = 'PublishedConnectionScheduledTaskTablesGrid';

const tablesGridClass = cssClass(cssPrefix, 'tablesGrid', flexColumnStyle, {
  position: 'relative',
  minHeight: getHeightForRows(2),
  paddingBottom: CommonStyles.Sizes.LargeSpace,
  width: '100%'
});

const emptyChildClass = cssClass(cssPrefix, 'emptyChild', {
  padding: `${CommonStyles.Sizes.SmallSpace}px ${CommonStyles.Sizes.MediumSpace}px`,
});

const styleOverride = {
  paddingLeft: 0,
};

enum PublishedConnectionScheduledTaskTablesGridTypes {
  TableName = 'tableName',
  RefreshType = 'refreshType',
}

function getColumns(): DataGrid.Column[] {
  const tableNameColumn: DataGrid.Column = {
    name: 'table-name-col',
    dataKey: PublishedConnectionScheduledTaskTablesGridTypes.TableName,
    sortable: false,
    growToFit: true,
    headerText: Messages.table(),
    headerFormatter: PlainTextHeader(),
    resizable: false,
    cellFormatter: PlainTextCell(PublishedConnectionScheduledTaskTablesGridTypes.TableName, 'tableNameCell', styleOverride)
  };
  const refreshTypeColumn: DataGrid.Column = {
    name: 'refresh-type-col',
    dataKey: PublishedConnectionScheduledTaskTablesGridTypes.RefreshType,
    sortable: false,
    growToFit: true,
    headerText: Messages.publishedConnectionRefreshType(),
    headerFormatter: PlainTextHeader(),
    resizable: false,
    cellFormatter: PlainTextCell(PublishedConnectionScheduledTaskTablesGridTypes.RefreshType, 'refreshTypeCell', styleOverride)
  };

  return [
    tableNameColumn,
    refreshTypeColumn
  ];
}

type PublishedConnectionScheduledTaskTablesGridRow = Readonly<{
  tableName: string;
  refreshType: string;
}>;

export type PublishedConnectionScheduledTaskTablesGridProps = Readonly<{
  tables: ReadonlyArray<PublishedConnectionTaskTableInfo>;
  emptyGridMessage?: string;
}>;

export function PublishedConnectionScheduledTaskTablesGrid(props: PublishedConnectionScheduledTaskTablesGridProps): JSX.Element {
  const { emptyGridMessage, tables } = props;

  const sliceable = React.useCallback(
    (orderClause: ReadonlyArray<IOrderClause>): ISliceable<PublishedConnectionScheduledTaskTablesGridRow> => {
      const tableRows: PublishedConnectionScheduledTaskTablesGridRow[] = tables.map((table) => {
        return {
          tableName: table.alias,
          refreshType: Messages.writeOptionsFull()
        };
      });

      return SliceableHelper.fromArray(tableRows);
    },
    [tables]
  );

  return (
    <div className={tablesGridClass} style={{ height: getHeightForRows(tables.length + 1) }}>
      <SortableDataGrid
        emptyChild={emptyGridMessage ? <div className={emptyChildClass}>{emptyGridMessage}</div> : null}
        columns={getColumns()}
        getSliceable={sliceable}
      />
    </div>
  );
}
